import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { EventMetricsService } from '../services/EventMetricsService';
import type { EventMetrics, SeriesMetrics } from '../types/organizer-events.types';

// Cache TTL: 5 minutes
const CACHE_TTL_MS = 5 * 60 * 1000;

// Debounce: minimum time between fetches
const FETCH_DEBOUNCE_MS = 1000;

// Loading flag to prevent concurrent fetches
let isLoadingMetrics = false;

interface OrganizerEventsState {
  // Metrics cache
  eventMetrics: Record<string, EventMetrics>;
  seriesMetrics: Record<string, SeriesMetrics>;

  // Loading states
  metricsLoading: boolean;
  metricsError: string | null;

  // Cache management
  lastFetchTime: number | null;
  organizerId: string | null;

  // Actions
  loadMetrics: (organizerId: string, force?: boolean) => Promise<void>;
  loadMetricsByEventIds: (eventIds: string[], force?: boolean) => Promise<void>;
  loadSeriesMetrics: (seriesId: string) => Promise<void>;
  getEventMetrics: (eventId: string) => EventMetrics | undefined;
  getSeriesMetrics: (seriesId: string) => SeriesMetrics | undefined;
  invalidateCache: () => void;
  isStale: () => boolean;
}

export const useOrganizerEventsStore = create<OrganizerEventsState>()((set, get) => ({
  // Initial state
  eventMetrics: {},
  seriesMetrics: {},
  metricsLoading: false,
  metricsError: null,
  lastFetchTime: null,
  organizerId: null,

  /**
   * Load metrics for all events of an organizer
   * Uses cache if not stale (5 min TTL)
   */
  loadMetrics: async (organizerId: string, force = false) => {
    const state = get();

    // Check if we already have fresh data for this organizer
    if (!force && state.organizerId === organizerId && !state.isStale()) {
      console.log('OrganizerEventsStore - Using cached metrics');
      return;
    }

    // Prevent concurrent fetches
    if (isLoadingMetrics) {
      console.log('OrganizerEventsStore - Fetch already in progress');
      return;
    }

    // Debounce check
    if (state.lastFetchTime && Date.now() - state.lastFetchTime < FETCH_DEBOUNCE_MS) {
      console.log('OrganizerEventsStore - Debounce active');
      return;
    }

    try {
      isLoadingMetrics = true;
      set({ metricsLoading: true, metricsError: null });

      console.log('OrganizerEventsStore - Loading metrics for:', organizerId);

      const metricsMap = await EventMetricsService.getOrganizerEventsMetrics(organizerId);

      set({
        eventMetrics: metricsMap,
        metricsLoading: false,
        lastFetchTime: Date.now(),
        organizerId
      });

      console.log('OrganizerEventsStore - Metrics loaded:', Object.keys(metricsMap).length, 'events');

    } catch (error) {
      console.error('OrganizerEventsStore - loadMetrics error:', error);
      set({
        metricsLoading: false,
        metricsError: error instanceof Error ? error.message : 'Failed to load metrics'
      });
    } finally {
      isLoadingMetrics = false;
    }
  },

  /**
   * Load metrics by event IDs (optimized - uses cached event list)
   * More efficient: no JOIN with events table, uses IDs we already have
   */
  loadMetricsByEventIds: async (eventIds: string[], force = false) => {
    const state = get();

    // Skip if no events
    if (!eventIds || eventIds.length === 0) {
      console.log('OrganizerEventsStore - No event IDs provided');
      return;
    }

    // Check if we already have fresh data
    if (!force && !state.isStale() && Object.keys(state.eventMetrics).length > 0) {
      console.log('OrganizerEventsStore - Using cached metrics');
      return;
    }

    // Prevent concurrent fetches
    if (isLoadingMetrics) {
      console.log('OrganizerEventsStore - Fetch already in progress');
      return;
    }

    // Debounce check
    if (state.lastFetchTime && Date.now() - state.lastFetchTime < FETCH_DEBOUNCE_MS) {
      console.log('OrganizerEventsStore - Debounce active');
      return;
    }

    try {
      isLoadingMetrics = true;
      set({ metricsLoading: true, metricsError: null });

      console.log(`OrganizerEventsStore - Loading metrics for ${eventIds.length} events (by IDs)`);

      const metricsMap = await EventMetricsService.getMetricsByEventIds(eventIds);

      set({
        eventMetrics: metricsMap,
        metricsLoading: false,
        lastFetchTime: Date.now()
      });

      console.log(`OrganizerEventsStore - Metrics loaded: ${Object.keys(metricsMap).length}/${eventIds.length} events`);

    } catch (error) {
      console.error('OrganizerEventsStore - loadMetricsByEventIds error:', error);
      set({
        metricsLoading: false,
        metricsError: error instanceof Error ? error.message : 'Failed to load metrics'
      });
    } finally {
      isLoadingMetrics = false;
    }
  },

  /**
   * Load metrics for a specific series (on-demand)
   */
  loadSeriesMetrics: async (seriesId: string) => {
    const state = get();

    // Check if already cached
    if (state.seriesMetrics[seriesId]) {
      return;
    }

    try {
      const metrics = await EventMetricsService.getSeriesMetrics(seriesId);

      if (metrics) {
        set((state) => ({
          seriesMetrics: {
            ...state.seriesMetrics,
            [seriesId]: metrics
          }
        }));
      }
    } catch (error) {
      console.error('OrganizerEventsStore - loadSeriesMetrics error:', error);
    }
  },

  /**
   * Get metrics for a specific event (from cache)
   */
  getEventMetrics: (eventId: string) => {
    return get().eventMetrics[eventId];
  },

  /**
   * Get metrics for a specific series (from cache)
   */
  getSeriesMetrics: (seriesId: string) => {
    return get().seriesMetrics[seriesId];
  },

  /**
   * Invalidate the cache (force refresh on next load)
   */
  invalidateCache: () => {
    set({
      eventMetrics: {},
      seriesMetrics: {},
      lastFetchTime: null
    });
  },

  /**
   * Check if cache is stale (older than TTL)
   */
  isStale: () => {
    const { lastFetchTime } = get();
    if (!lastFetchTime) return true;
    return Date.now() - lastFetchTime > CACHE_TTL_MS;
  }
}));

// ===== SELECTOR HOOKS (useShallow for performance) =====

/**
 * Hook to get metrics for a specific series
 */
export const useSeriesMetrics = (seriesId: string) => {
  return useOrganizerEventsStore(
    useShallow((state) => state.seriesMetrics[seriesId])
  );
};

